import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import './MercadoPagoAdmin.css';

const MercadoPagoAdmin = ({ darkMode }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pagado'); // all, pagado, procesando, pendiente, rechazado
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'compras_mercadopago'),
        orderBy('date', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Convertir timestamp de Firestore a Date
          date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
          lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : (data.lastUpdated ? new Date(data.lastUpdated) : null)
        };
      });
      
      setOrders(ordersData);
      console.log('Órdenes cargadas:', ordersData.length);
      console.log('Sample order:', ordersData[0]); // Para debug
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Intl.DateTimeFormat('es-AR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '$0.00';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price);
  };

  const getDetailedStatus = (order) => {
    const status = order.status?.toLowerCase() || '';
    const paymentStatus = order.paymentStatus?.toLowerCase() || '';
    
    // Priorizar paymentStatus si existe, sino usar status
    if (paymentStatus) {
      switch (paymentStatus) {
        case 'approved':
          return { type: 'pagado', label: 'Pagado', emoji: '✅', color: 'paid' };
        case 'pending':
          return { type: 'procesando', label: 'Pendiente', emoji: '⏳', color: 'pending' };
        case 'rejected':
          return { type: 'rechazado', label: 'Rechazado', emoji: '❌', color: 'rejected' };
        case 'cancelled':
          return { type: 'rechazado', label: 'Cancelado', emoji: '🚫', color: 'rejected' };
        case 'in_process':
          return { type: 'procesando', label: 'En Proceso', emoji: '🔄', color: 'pending' };
        default:
          return { type: 'pendiente', label: paymentStatus, emoji: '❓', color: 'unknown' };
      }
    }
    
    // Si no hay paymentStatus, usar status
    if (status) {
      switch (status) {
        case 'pagado':
          return { type: 'pagado', label: 'Pagado', emoji: '✅', color: 'paid' };
        case 'procesando':
          return { type: 'procesando', label: 'Procesando', emoji: '⏳', color: 'pending' };
        default:
          return { type: 'pendiente', label: status, emoji: '❓', color: 'unknown' };
      }
    }
    
    return { type: 'pendiente', label: 'Sin estado', emoji: '⚪', color: 'unknown' };
  };

  const getStatusBadge = (order) => {
    const statusInfo = getDetailedStatus(order);
    return (
      <span className={`status-badge ${statusInfo.color}`}>
        {statusInfo.emoji} {statusInfo.label}
      </span>
    );
  };

  const getPaymentStatusDisplay = (order) => {
    if (order.paymentStatus) {
      const statusMap = {
        'approved': { text: 'Aprobado', class: 'approved' },
        'pending': { text: 'Pendiente', class: 'pending' },
        'rejected': { text: 'Rechazado', class: 'rejected' },
        'cancelled': { text: 'Cancelado', class: 'cancelled' },
        'in_process': { text: 'En Proceso', class: 'processing' }
      };
      
      const statusInfo = statusMap[order.paymentStatus.toLowerCase()] || 
                        { text: order.paymentStatus, class: 'unknown' };
      
      return (
        <span className={`payment-status-badge ${statusInfo.class}`}>
          {statusInfo.text}
        </span>
      );
    }
    
    return <span className="payment-status-badge empty">Sin dato</span>;
  };

  const filteredOrders = orders.filter(order => {
    // Filtro por estado
    if (filter !== 'all') {
      const statusInfo = getDetailedStatus(order);
      if (filter !== statusInfo.type) return false;
    }
    
    // Filtro por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        order.nombre?.toLowerCase().includes(term) ||
        order.email?.toLowerCase().includes(term) ||
        order.orderNumber?.toString().includes(term) ||
        order.paymentId?.toLowerCase().includes(term) ||
        order.id?.toLowerCase().includes(term) ||
        order.status?.toLowerCase().includes(term) ||
        order.paymentStatus?.toLowerCase().includes(term)
      );
    }
    
    return true;
  });

  const stats = {
    total: orders.length,
    pagado: orders.filter(o => getDetailedStatus(o).type === 'pagado').length,
    procesando: orders.filter(o => getDetailedStatus(o).type === 'procesando').length,
    pendiente: orders.filter(o => getDetailedStatus(o).type === 'pendiente').length,
    rechazado: orders.filter(o => getDetailedStatus(o).type === 'rechazado').length,
    totalRevenue: orders
      .filter(o => getDetailedStatus(o).type === 'pagado')
      .reduce((sum, o) => sum + (o.totalPrice || 0), 0)
  };

  // Componente Tooltip
  const Tooltip = ({ text, children, position = 'top' }) => {
    const [show, setShow] = useState(false);
    
    return (
      <div 
        className="mp-tooltip-container"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
        {show && (
          <div className={`mp-tooltip mp-tooltip-${position}`}>
            {text}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="mp-loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Cargando órdenes de MercadoPago...</div>
      </div>
    );
  }

  return (
    <div className="mercadopago-admin">
      {/* Header */}
      <div className="mp-header">
        <div className="mp-header-content">
          <div className="mp-header-text">
            <h1>💳 MercadoPago - Estado de Pagos</h1>
            <p>Visualiza el estado de las órdenes según webhooks de MercadoPago</p>
          </div>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="mp-stats">
        <div className="stat-card">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Total Órdenes</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.pagado}</div>
          <div className="stat-label">Pagadas</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.procesando}</div>
          <div className="stat-label">Procesando</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.pendiente}</div>
          <div className="stat-label">Pendientes</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.rechazado}</div>
          <div className="stat-label">Rechazadas</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{formatPrice(stats.totalRevenue)}</div>
          <div className="stat-label">Ingresos Confirmados</div>
        </div>
      </div>

      {/* Controles */}
      <div className="controls">
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Todas ({stats.total})
          </button>
          <button 
            className={`filter-btn ${filter === 'pagado' ? 'active' : ''}`}
            onClick={() => setFilter('pagado')}
          >
            Pagadas ({stats.pagado})
          </button>
          <button 
            className={`filter-btn ${filter === 'procesando' ? 'active' : ''}`}
            onClick={() => setFilter('procesando')}
          >
            Procesando ({stats.procesando})
          </button>
          <button 
            className={`filter-btn ${filter === 'pendiente' ? 'active' : ''}`}
            onClick={() => setFilter('pendiente')}
          >
            Pendientes ({stats.pendiente})
          </button>
          <button 
            className={`filter-btn ${filter === 'rechazado' ? 'active' : ''}`}
            onClick={() => setFilter('rechazado')}
          >
            Rechazadas ({stats.rechazado})
          </button>
        </div>
        
        <div className="search-and-refresh">
          <input
            type="text"
            placeholder="Buscar por nombre, email, orden, payment ID, estado..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          
          <button 
            onClick={fetchOrders}
            className="refresh-btn"
            disabled={loading}
          >
            🔄 Actualizar
          </button>
        </div>
      </div>

      {/* Tabla de órdenes */}
      {filteredOrders.length === 0 ? (
        <div className="empty-state">
          <h3>📭 No hay órdenes que mostrar</h3>
          <p>
            {searchTerm 
              ? `No se encontraron órdenes que coincidan con "${searchTerm}"`
              : 'No se encontraron órdenes con los filtros seleccionados.'
            }
          </p>
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="clear-search-btn"
            >
              Limpiar búsqueda
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Orden #</th>
                <th>Cliente</th>
                <th>Email</th>
                <th>Fecha</th>
                <th>Productos</th>
                <th>Total</th>
                <th>Estado Final</th>
                <th>Payment Status</th>
                <th>Payment ID</th>
                <th>Datos Adicionales</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => (
                <tr key={order.id}>
                  <td>
                    <div className="order-number">
                      <strong>#{order.orderNumber || 'N/A'}</strong>
                      {order.manuallyUpdated && (
                        <div className="manual-badge" title="Modificado manualmente">
                          ✏️ Manual
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="customer-info">
                      <strong>{order.nombre || 'N/A'}</strong>
                      <div className="phone">
                        📞 {order.telefono || 'N/A'}
                      </div>
                      <div className="location">
                        📍 {order.localidad || 'N/A'}, {order.provincia || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="email">{order.email || 'N/A'}</td>
                  <td>
                    <div className="date-info">
                      <div>{formatDate(order.date)}</div>
                      {order.lastUpdated && (
                        <div className="last-updated">
                          Actualizado: {formatDate(order.lastUpdated)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="order-items">
                      {order.cartItems && order.cartItems.length > 0 ? (
                        order.cartItems.map((item, idx) => (
                          <div key={idx} className="order-item">
                            <strong>{item.model || 'Producto'}</strong> x{item.quantity || 1}
                            <div className="item-price">
                              {formatPrice(item.price || item.costPrice || 0)}
                            </div>
                          </div>
                        ))
                      ) : (
                        <span className="no-items">Sin productos</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="price-info">
                      <strong>{formatPrice(order.totalPrice || 0)}</strong>
                      {order.shippingCost > 0 && (
                        <div className="shipping">
                          + Envío: {formatPrice(order.shippingCost)}
                        </div>
                      )}
                      {order.discountAmount > 0 && (
                        <div className="discount">
                          - Descuento: {formatPrice(order.discountAmount)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{getStatusBadge(order)}</td>
                  <td>{getPaymentStatusDisplay(order)}</td>
                  <td>
                    {order.paymentId ? (
                      <div className="payment-id" title={order.paymentId}>
                        <span className="payment-id-short">
                          {order.paymentId.length > 15 
                            ? `${order.paymentId.substring(0, 15)}...`
                            : order.paymentId
                          }
                        </span>
                      </div>
                    ) : (
                      <span className="no-payment-id">Sin Payment ID</span>
                    )}
                  </td>
                  <td>
                    <div className="additional-info">
                      {order.isActive !== undefined && (
                        <div className={`active-status ${order.isActive ? 'active' : 'inactive'}`}>
                          {order.isActive ? '🟢 Activo' : '🔴 Inactivo'}
                        </div>
                      )}
                      {order.informacionAdicional && (
                        <div className="extra-info" title={order.informacionAdicional}>
                          💬 Info adicional
                        </div>
                      )}
                      {order.manualUpdateBy && (
                        <div className="manual-update-info">
                          👤 {order.manualUpdateBy}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer con información */}
      <div className="mp-footer">
        <p>
          💡 Los estados se actualizan automáticamente mediante webhooks de MercadoPago. 
          Esta vista es solo de lectura y refleja el estado real de los pagos.
        </p>
        <p>
          🔍 Total de órdenes: {orders.length} | 
          Con Payment ID: {orders.filter(o => o.paymentId).length} | 
          Con Payment Status: {orders.filter(o => o.paymentStatus).length}
        </p>
      </div>
    </div>
  );
};

export default MercadoPagoAdmin;